import {
  AuditOutlined,
  CalculatorOutlined,
  DashboardOutlined,
  FileTextOutlined,
  LogoutOutlined,
  RiseOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Avatar, Dropdown, Grid, Layout, Menu, Tag, Typography } from 'antd';
import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import LanguageSwitch from './common/LanguageSwitch';
import PageTranslator from './common/PageTranslator';
import BrandLogo from './common/BrandLogo';
import { palette } from '../theme/salesTheme';
import type { Role } from '../types';

const { Header, Sider, Content } = Layout;

interface NavItem {
  key: string;
  icon: React.ReactNode;
  labelKey: string;
  roles: Role[];
}

const NAV: NavItem[] = [
  { key: '/dashboard', icon: <DashboardOutlined />, labelKey: 'nav.investmentDashboard', roles: ['Admin', 'Accountant', 'Viewer'] },
  { key: '/investors', icon: <TeamOutlined />, labelKey: 'nav.investors', roles: ['Admin', 'Accountant', 'Viewer'] },
  // Split the same way the inbound side is: a KPI dashboard and a searchable list, sitting right
  // next to their inbound counterparts. Access stays Admin-only, matching the API.
  { key: '/my-investments', icon: <RiseOutlined />, labelKey: 'nav.myInvestmentDashboard', roles: ['Admin'] },
  { key: '/dealers', icon: <TeamOutlined />, labelKey: 'nav.myInvestments', roles: ['Admin'] },
  { key: '/portal', icon: <WalletOutlined />, labelKey: 'nav.myPortfolio', roles: ['Investor'] },
  { key: '/reports', icon: <FileTextOutlined />, labelKey: 'nav.reports', roles: ['Admin', 'Accountant', 'Viewer'] },
  { key: '/calculator', icon: <CalculatorOutlined />, labelKey: 'nav.calculator', roles: ['Admin', 'Accountant', 'Viewer', 'Investor'] },
  { key: '/users', icon: <UserOutlined />, labelKey: 'nav.users', roles: ['Admin'] },
  { key: '/audit-log', icon: <AuditOutlined />, labelKey: 'nav.auditLog', roles: ['Admin'] },
  { key: '/dealer-portal', icon: <WalletOutlined />, labelKey: 'nav.myInvestments', roles: ['Dealer'] },
];

/** Template theme colours, so role chips sit in the same palette as the rest of the UI. */
const ROLE_COLOR: Record<Role, string> = {
  Admin: palette.danger,
  Accountant: palette.info,
  Viewer: 'default',
  Investor: palette.success,
  Dealer: palette.purple,
};

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const [collapsed, setCollapsed] = useState(false);

  const isMobile = !screens.md;
  const items = NAV.filter((item) => user && item.roles.includes(user.role));

  // Highlights the parent entry when a detail route (/investors/12) is open.
  const selected = items
    .map((i) => i.key)
    .filter((key) => location.pathname === key || location.pathname.startsWith(`${key}/`));

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <PageTranslator />
      <Sider
        breakpoint="md"
        collapsedWidth={isMobile ? 0 : 80}
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        theme="light"
        width={240}
      >
        <div className="app-brand">
          <BrandLogo />
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={selected}
          items={items.map(({ key, icon, labelKey }) => ({ key, icon, label: t(labelKey) }))}
          onClick={({ key }) => {
            navigate(key);
            if (isMobile) setCollapsed(true);
          }}
        />
      </Sider>

      <Layout>
        <Header
          style={{
            background: palette.white,
            padding: '0 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 12,
          }}
        >
          {/* Admin-only: the Urdu rendering is still being reviewed, so it is not exposed to
              investors, dealers or other staff until the wording is signed off. */}
          {user?.role === 'Admin' && <LanguageSwitch />}
          {user && <Tag color={ROLE_COLOR[user.role]}>{t(`roles.${user.role}`)}</Tag>}
          <Dropdown
            menu={{
              items: [{ key: 'logout', icon: <LogoutOutlined />, label: t('nav.logOut'), danger: true }],
              onClick: () => {
                logout();
                navigate('/login', { replace: true });
              },
            }}
          >
            <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar size="small" icon={<UserOutlined />} style={{ background: palette.primary }} />
              {!isMobile && (
                <Typography.Text strong data-no-translate>
                  {user?.fullName}
                </Typography.Text>
              )}
            </span>
          </Dropdown>
        </Header>

        <Content style={{ margin: isMobile ? 12 : 24 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
